import { Icon, Typography } from '@mui/material';

import axios from 'axios';
import JSPDF from 'jspdf';
import _, { orderBy, uniq } from 'lodash';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { IMultiSelectOption } from 'common/api/type-def';
import {
	ACTIVITY_TYPE,
	DEFAULT_TENANT_ID,
	ENABLE_BANNER_MODULES,
	GRID_VIEW_PAGE_SIZE,
	mimeTypes,
	RESPONSE_SUBMITTED_TEXT,
	TABLE_VIEW_PAGE_SIZE,
} from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { LoggedInRole } from 'common/constants/UserManagementConstants';
import localStorageWrapper from 'common/loginUtils/local-storage-wrapper';
import storageWrapper from 'common/loginUtils/storage-wrapper';

import { getDownloadFileUrl } from 'features/AiSimulation/api';
import { IApplyFilterParam } from 'features/RiskProfile/Taxonomy/RiskReferenceTaxonomy/api/type-def';
import { IUserSelectListOptions } from 'features/Settings/api/type-def';
import { IUser } from 'features/Settings/UserManagement/api/type-def';

import { getUserByUserId } from '../../features/Settings/UserManagement/api';

import { tableSortIcon } from '../assets/images';
import { Order } from '../components/Table/CommonTableStyles';

import styles from './utilFunction.module.scss';

const { scrollbarTrack, scrollbar } = styles;

interface ISelectProps {
	label: number | string;
	value: number | string;
}

const { OK, BAD_REQUEST, NOT_FOUND, INTERNAL_SERVER_ERROR } = HTTP_STATUS_CODES;
const { TABLE_SIZE_12, TABLE_SIZE_24, TABLE_SIZE_48 } = TABLE_VIEW_PAGE_SIZE;
const { GRID_SIZE_10, GRID_SIZE_25, GRID_SIZE_50 } = GRID_VIEW_PAGE_SIZE;
const { ROLE_INVENTORY_APPROVER, ROLE_INVENTORY_MANAGER } = LoggedInRole;
const OCP_APIM_KEY = process.env.REACT_APP_OCP_APIM_AI_SIMULATION_KEY as string;

export const ImgTagComponent = (src: any, alt: string, className?: string) => (
	<img src={src} alt={alt} className={className} />
);

const getNumbersList = (limit: number) =>
	[...Array(limit)].map((a, b) => (b <= 9 ? `0${b}` : b));

export const getValuesforSelect = (limit: number) => {
	const list = getNumbersList(limit);
	const finalList: ISelectProps[] = [];
	list.forEach((el) => finalList.push({ value: el, label: el }));
	return finalList;
};

export const formatAssigneeName = (name: string) => {
	if (name) {
		const words = name.split(' ');
		const result = words.map((words) => words.charAt(0).toUpperCase()).join('');
		return result;
	}
	return '';
};

export const scrollProps = (
	direction: 'x-axis' | 'y-axis' = 'y-axis',
	trackBackGround: string = scrollbarTrack,
) => {
	const scrollStyle =
		direction === 'y-axis' ? { width: '0.4em' } : { height: '8px' };
	return {
		'&::-webkit-scrollbar': { ...scrollStyle },
		'&::-webkit-scrollbar-track': {
			background: trackBackGround,
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: scrollbar,
		},
		'&::-webkit-scrollbar-thumb:hover': {
			background: scrollbar,
		},
	};
};

export function hasChildren(item: any) {
	const { items: children } = item;

	if (children === undefined) {
		return false;
	}

	if (children.constructor !== Array) {
		return false;
	}

	if (children.length === 0) {
		return false;
	}

	return true;
}

export const truncateString = (str: string, length: number) =>
	`${str}`.slice(0, length);

export const getAvatarData = (name: string) => {
	if (name) {
		const username = name.split('@');
		const words = username[0].toUpperCase().split('.');
		const result = words.map((words) => words.charAt(0)).join('');
		return result;
	}
	return null;
};

export const pascalFormat = (displayName: string) => {
	const response =
		(displayName && displayName[0].toUpperCase() + displayName.slice(1)) || '';
	return response;
};

export function stableSort<T>(
	array: readonly T[],
	comparator: (a: T, b: T) => number,
) {
	const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}
export function descendingComparator<T extends Record<string, any>>(
	a: T,
	b: T,
	orderBy: keyof T,
) {
	if (typeof b[orderBy] === 'string' && typeof a[orderBy] === 'string') {
		const bVal: string = b[orderBy];
		const aVal: string = a[orderBy];
		const bValue = _.lowerCase(bVal);
		const aValue = _.lowerCase(aVal);
		return bValue.localeCompare(aValue);
	}
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}
export function getComparator<Key extends keyof any>(
	order: Order,
	orderBy: Key,
): (
	a: { [key in Key]: number | string },
	b: { [key in Key]: number | string },
) => number {
	return order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

export const dateInCorrectFormat = (time: string) => {
	const date = time ? new Date(time) : '';
	const formattedDate = date.toLocaleString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: '2-digit',
	});
	return formattedDate;
};

export const dateInCorrectFormatWithFullYear = (time: string) => {
	const date = time ? new Date(time) : '';
	const formattedDate = date.toLocaleString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	});
	return formattedDate;
};

export const dateInCorrectFormatWithFullYearTime = (time: string) => {
	const date = time ? new Date(time) : '';
	const formattedDate = date.toLocaleString('en-US', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
	return formattedDate;
};

export const startsWithDigitDate = (str: string) => /^\d/.test(str);

export function getCurrentDate(): string {
	const today = new Date();
	const year = today.getFullYear().toString();
	const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based, so add 1
	const day = today.getDate().toString().padStart(2, '0');

	return `${year}-${month}-${day}`;
}

export const dateFormatForGPT = (time: string) => {
	let res;
	const date = time ? new Date(time) : '';
	const todayDate = new Date();
	const formattedDate = date.toLocaleString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	});
	const formattedDateToday = todayDate.toLocaleString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	});
	if (formattedDate === formattedDateToday) {
		res = date.toLocaleString('en-US', {
			hour: 'numeric',
			minute: 'numeric',
			month: '2-digit',
			day: '2-digit',
			year: 'numeric',
			hour12: true,
		});
	} else {
		res = date.toLocaleString('en-US', {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric',
		});
	}
	return res;
};

export const getQueryParams = (url = window.location.search) => {
	const search = url.includes('?') ? url.split('?')[1] : '';
	return (
		(search &&
			JSON.parse(
				`{"${search.replace(/&/g, '","').replace(/=/g, '":"')}"}`,
				(key, value) => (key === '' ? value : decodeURIComponent(value)),
			)) ||
		''
	);
};

export const getUserEmail = async (userId: string) => {
	const response = await getUserByUserId(userId);
	if (response?.responseBody) {
		return {
			email: response.responseBody.email,
			userName: `${response.responseBody.firstName} ${response.responseBody.lastName}`,
		};
	}
	return '';
};

// function to convert the Data of type Date to string (format to YYYY-MM-DD)
export const formatDate = (date: Date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

// function to convert the Data of type Date to string (format to MMMMM DD, yyyy)
export const dateLongFormat = (time: string) => {
	const date = time ? new Date(time) : '';
	const formattedDate = date.toLocaleString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	});

	return formattedDate;
};

// function to convert the Data of type Date to string (format to M/d h:m AM/PM )
export const dateTimeShortFormat = (time: string) => {
	const date = time ? new Date(time) : '';
	const formattedDate = date.toLocaleString('en-US', {
		month: 'numeric',
		day: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
	});

	return formattedDate.replace(/,/g, '');
};

// function to convert the Data of type Date to string (format to MM/DD/yyyy)
export const dateFullYearFormat = (time: string) => {
	const date = time ? new Date(time) : '';
	const formattedDate = date.toLocaleString('en-US', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	});

	return formattedDate;
};

// function to calculate end date as per the options selected in dropdown
export const calculateDuration = (
	startDate: Date,
	endDate: Date,
	selectedOption: string,
) => {
	switch (selectedOption) {
		case 'View by: Last 3 Months':
			endDate.setMonth(startDate.getMonth() - 3);
			break;

		case 'View by: Last Month':
			endDate.setMonth(startDate.getMonth() - 1);
			break;

		case 'View by: Last Week':
			endDate.setDate(startDate.getDate() - 7);
			break;

		default:
			endDate.setMonth(startDate.getMonth() - 6);
			break;
	}
	return endDate;
};
// convertDateforApi required to pass the params in string like 'MM/DD/YY-MM/DD/YY' example -'10/22/24-10/22/24'
export const convertDateforApi = (dateRange: string) => {
	if (!dateRange) return [];

	const [startDate, endDate] = dateRange
		.split('-')
		.map((date) => new Date(date));

	const formatStart = getDateStringFromISOString(
		getISODateString(new Date().toISOString()),
	);
	const formatEnd = getDateStringFromISOString(
		getISODateString(new Date().toISOString()),
	);

	return startDate.getTime() === endDate.getTime()
		? [formatStart, formatEnd]
		: [formatDate(startDate), formatDate(endDate)];
};
// getFormatedDateRange option to be as as Today, This week
export const getFormatedDateRange = (option: string) => {
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();
	const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

	const { startDate, endDate } = setDateRange(
		option,
		currentDate,
		currentYear,
		currentMonth,
		lastDayOfMonth,
	);
	const formattedStartDate = formatDateRange(startDate);
	const formattedEndDate = formatDateRange(endDate);

	return `${formattedStartDate}-${formattedEndDate}`;
};
// formatDateRange required to pass date format in MM/DD/YY
export const formatDateRange = (date: Date) =>
	date.toLocaleDateString('en-US', {
		year: '2-digit',
		month: 'numeric',
		day: 'numeric',
	});
export const setDateRange = (
	option: any,
	currentDate: {
		setDate: (arg0: number) => string | number | Date;
		getDate: () => number;
		getDay: () => number;
	},
	currentYear: number,
	currentMonth: number,
	lastDayOfMonth: string | number | Date,
) => {
	const { THIS_WEEK, THIS_MONTH, LAST_3_MONTH, LAST_6_MONTH, THIS_YEAR } =
		ACTIVITY_TYPE;
	let startDate = null;
	let endDate = null;

	switch (option) {
		case THIS_WEEK: {
			const sunday = new Date(
				currentDate.setDate(currentDate.getDate() - currentDate.getDay()),
			);
			const saturday = new Date(sunday);
			saturday.setDate(sunday.getDate() + 6);
			startDate = new Date(sunday);
			endDate = new Date(saturday);
			break;
		}
		case THIS_MONTH: {
			const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
			startDate = new Date(firstDayOfMonth);
			endDate = new Date(lastDayOfMonth);
			break;
		}
		case LAST_3_MONTH: {
			const threeMonthsAgo = new Date(currentYear, currentMonth - 2, 1);
			startDate = new Date(threeMonthsAgo);
			endDate = new Date(lastDayOfMonth);
			break;
		}
		case LAST_6_MONTH: {
			const sixMonthsAgo = new Date(currentYear, currentMonth - 5, 1);
			startDate = new Date(sixMonthsAgo);
			endDate = new Date(lastDayOfMonth);
			break;
		}
		case THIS_YEAR: {
			const firstDayOfYear = new Date(currentYear, 0, 1);
			startDate = new Date(firstDayOfYear);
			endDate = new Date(lastDayOfMonth);
			break;
		}
		default: {
			startDate = new Date();
			endDate = new Date();
			break;
		}
	}

	return { startDate, endDate };
};

export const scrollToTop = () =>
	window.scrollTo({
		top: 0,
		left: 0,
		behavior: 'smooth',
	});

export const sortIcon = () => (
	<Icon>
		<img src={tableSortIcon} alt="" />
	</Icon>
);

export const swapArrayItems = (arr: any, from: number, to: number) => {
	const temp = arr[to];
	arr[to] = arr[from];
	arr[from] = temp;

	return arr;
};

export const swapArrayObjects = (arr: any, fromObj: any, toObj: any) => {
	const fromindex = arr
		.map((i: any) => i.orderNumber)
		.indexOf(fromObj.orderNumber);
	const toindex = arr.map((i: any) => i.orderNumber).indexOf(toObj.orderNumber);
	const tempOrder = arr[fromindex].orderNumber;
	arr[fromindex].orderNumber = arr[toindex].orderNumber;
	arr[toindex].orderNumber = tempOrder;
	const sortedArray = _.sortBy(arr, ['orderNumber']);
	const paramsObj = [arr[fromindex], arr[toindex]];
	return { sortedArray, paramsObj };
};
export const formatNumber = (number: number) =>
	new Intl.NumberFormat('en-US').format(number);

export const arrayToCsv = (data: any) =>
	`${Object.keys(data[0]).join(',')}\n${data
		.map((d: any) => Object.values(d).join(','))
		.join('\n')}`;
export const getFileName = () => {
	// Setting the anchor tag attribute for downloading
	// and passing the download file name
	const dateStr = moment(new Date()).format('MM-DD-YYYY HH:mm:ss');
	const fileName = `TestScript_${dateStr}`;

	const initialFileName = fileName
		.split('-')
		.map((ele: string) => ele.trim())
		.join('');
	const initialResetFileName = initialFileName
		.split(':')
		.map((ele: string) => ele.trim())
		.join('');
	const finalName = initialResetFileName
		.split(' ')
		.map((ele: string) => ele.trim())
		.join('');

	return finalName;
};

export const parseHtml = (html: any) => {
	const boldRegex = /<b>(.*?)<\/b>/g;
	const italicRegex = /<i>(.*?)<\/i>/g;
	const underlineRegex = /<u>(.*?)<\/u>/g;
	const result = {
		text: removeHTMLTags(html),
		bold: boldRegex.test(html),
		italic: italicRegex.test(html),
		underline: underlineRegex.test(html),
	};
	return result;
};

export const removeHTMLTags = (value: string) => {
	const safeValue = typeof value === 'string' ? value : String(value || '');
	const parser = new DOMParser();
	// Parse the HTML string
	const doc = parser.parseFromString(safeValue, 'text/html');
	// Extract text content
	const textContent = doc.body.textContent || '';
	// Trim whitespace
	const updatedText = textContent.replace('  ', ' ');

	return updatedText.split('\n').join('').split('\r').join('');
};

export const downloadCsvFileData = (
	data: any,
	fileName: string,
	fileType?: string,
	headers?: string[],
	sortedKeys?: string[],
) => {
	const worksheet = XLSX.utils.json_to_sheet(data, {
		header: sortedKeys,
	});
	data?.forEach((row: any, rowIndex: number) => {
		sortedKeys?.forEach((key, colIndex) => {
			const parsed = parseHtml(row[key]);
			const cellRef = XLSX.utils.encode_cell({ c: colIndex, r: rowIndex + 1 });
			worksheet[cellRef] = {
				v: parsed.text,
				s: {
					font: {
						bold: parsed.bold,
						italic: parsed.italic,
						underline: !!parsed.underline,
					},
				},
			};
		});
	});
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
	if (headers) {
		XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
	}
	XLSX.writeFile(workbook, `${fileName}.${fileType || 'csv'}`);
};

export const formatToLabelValueFormat = (obj: any, labelName?: string) => {
	const newObj = { label: '', value: '', checked: false, subOptions: [] };
	if (labelName) {
		newObj.label = obj[labelName];
	} else {
		newObj.label = obj.name;
	}
	newObj.value = obj.code ? obj.code : obj.name || obj.id;

	return newObj;
};

export const getCheckedValues = (option: IMultiSelectOption[]) => {
	const checkedValues: string[] = [];

	option.forEach((ele) => {
		if (ele.checked) {
			checkedValues.push(ele.value);
		} else if (ele.indeterminate && ele.subOptions) {
			ele.subOptions.forEach((subEle) => {
				checkedValues.push(subEle.value);
			});
		}
	});
	return checkedValues;
};

export const labelValueFormatOptions = (name: string) => {
	const newObj = { label: '', value: '', checked: false, subOptions: [] };
	newObj.label = name;
	newObj.value = name;

	return newObj;
};

export const extractInnermostHierarchy = (arr: any): any[] => {
	if (Array.isArray(arr) && arr.length === 1) {
		return extractInnermostHierarchy(arr[0]);
	}
	return Array.isArray(arr) ? arr : [arr];
};

export const extractValues = (arr: any[]): any[] =>
	arr.map((obj) => {
		obj.values = extractInnermostHierarchy(obj.values);
		return obj;
	});

export const getJursidictionCheckedValues = (option: IMultiSelectOption[]) => {
	const checkedValues: string[] = [];
	option.forEach((ele: any) => {
		if (ele.checked) {
			if (ele.subOptions?.length > 0) {
				ele.subOptions.forEach((subEle: any) => {
					checkedValues.push(subEle?.value);
				});
			} else {
				checkedValues.push(ele.value);
			}
		} else if (ele.indeterminate && ele.subOptions) {
			ele.subOptions.forEach((subEle: any) => {
				checkedValues.push(subEle?.value);
			});
		}
	});
	return checkedValues;
};

export const getCamelCaseString = (sentence: string) => {
	const words = sentence.split(' ');

	for (let i = 0; i < words.length; i += 1) {
		words[i] = words[i][0].toUpperCase() + words[i].substring(1);
	}

	return words.join(' ');
};

export const addUsersToStateList = (data: any, isEdit?: boolean) => {
	const mailIds: IUserSelectListOptions[] = data.map((ele: any) => {
		const tempobj = {} as any;
		tempobj.id = ele.email;
		tempobj.displayLabel = `${ele.firstName}`;
		tempobj.email = ele.email;
		tempobj.userName = ele.email;
		tempobj.firstName = ele.firstName;
		tempobj.lastName = ele.lastName;
		tempobj.status = ele.status;
		tempobj.checked = false;
		tempobj.userId = ele.email;
		tempobj.label = `${ele.firstName} ${ele.lastName}`;
		tempobj.isExisting = !!isEdit;
		return tempobj;
	});
	return mailIds;
};

export const formatSelectValue = (value: string) =>
	value === 'Select an option' ? '' : value;

export const saveFile = (blob: Blob, filename: string) => {
	const sanitizedFilename = filename.replace(/[^a-zA-Z0-9.\-_]/g, '');
	const downloadLink = document.createElement('a');
	const url = window.URL.createObjectURL(blob);
	try {
		downloadLink.href = url;
		downloadLink.download = sanitizedFilename;
		document.body.appendChild(downloadLink);
		downloadLink.click();
	} finally {
		document.body.removeChild(downloadLink);
	}
};

export const getTablePageSize = (size: number) => {
	let pageSize;
	switch (size) {
		case GRID_SIZE_10:
			pageSize = TABLE_SIZE_12;
			break;
		case GRID_SIZE_25:
			pageSize = TABLE_SIZE_24;
			break;
		case GRID_SIZE_50:
			pageSize = TABLE_SIZE_48;
			break;

		default:
			pageSize = TABLE_SIZE_12;
			break;
	}

	return pageSize;
};

export const getGridPageSize = (size: number) => {
	let pageSize;
	switch (size) {
		case TABLE_SIZE_12:
			pageSize = GRID_SIZE_10;
			break;
		case TABLE_SIZE_24:
			pageSize = GRID_SIZE_25;
			break;
		case TABLE_SIZE_48:
			pageSize = GRID_SIZE_50;
			break;

		default:
			pageSize = GRID_SIZE_10;
			break;
	}

	return pageSize;
};

export const enableBannerPage = (currentRoute: string) => {
	const enableBanner = ENABLE_BANNER_MODULES.includes(currentRoute);

	return enableBanner;
};

export const enableBackgroundGradientFlag = (currentRoute: string) => {
	let enableBackgroundColor = false;
	ENABLE_BANNER_MODULES.forEach((ele: any) => {
		if (currentRoute.startsWith(urlWithTenant(ele))) {
			enableBackgroundColor = true;
		}
	});
	return enableBackgroundColor;
};

export const isModuleEnabled = (
	featureFlag: boolean,
	isModuleAllowed: boolean,
): boolean => featureFlag && isModuleAllowed;

export const generateToastMessage = (response: any) => {
	if (response) {
		if (response.statusCode === OK) {
			return {
				message: 'Details saved successfully',
				severity: 'success',
			};
		}
		if (
			response.statusCode === BAD_REQUEST ||
			response.statusCode === NOT_FOUND ||
			response.statusCode === INTERNAL_SERVER_ERROR
		) {
			if (response?.errorBody || response?.error) {
				const { errorBody } = response;
				const responseError = response.error;
				const errorMsg =
					(errorBody?.errorMessage as string) ||
					(responseError?.message as string);
				return {
					message: errorMsg,
					severity: 'error',
				};
			}
		}
	}
	return null;
};

export const capitalizeName = (name: string) =>
	name ? name[0].toUpperCase() + name.slice(1) : '';

export const capitalizeEachWord = (status: string) => {
	const wordList = status.split(' ');
	return wordList.map((word) => capitalizeName(word)).join(' ');
};

export const removeUnexpectedFirstChar = (item: string) => {
	let alphanumericIndex = 0;
	for (let i = 0; i < item.length; i += 1) {
		const char = item.charAt(i);
		if (char.match(/^[a-z0-9]+$/i)) {
			alphanumericIndex = i;
			break;
		}
	}
	return item.substring(alphanumericIndex);
};

export const toSentenceCase = (str: string) => {
	if (str) {
		const newString = str
			.toLowerCase()
			.replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase())
			.replace(/english/g, () => 'English');

		return newString;
	}
	return str;
};

export const getUniqueValuesFromCommaSeperatedString = (str: string) => {
	if (str) {
		const items = str.split(', ');
		const uniqueItems = Array.from(new Set(items));
		return uniqueItems.join(', ');
	}
	return str;
};

export const getAdvanceFilterPayloadData = (
	allRelations: any,
	filtersData: any,
) => {
	if (allRelations?.length > 0) {
		allRelations.forEach((ele: any) => {
			const filterTitleObj = filtersData.filter(
				(iele: any) => iele?.filterKey === ele?.objectName,
			);
			ele.filterTitle = filterTitleObj[0]?.filterName;
			ele.filterKey = ele.objectName;
			ele.searchFlag = false;

			const allValues: any = [];
			const val = orderBy(uniq(ele.objectValues));
			if (val?.length > 0) {
				val.forEach((item: any, index: number) => {
					const obj: any = {
						label: item,
						value: item + index,
						checked: false,
						type: ele.objectName,
					};
					allValues.push(obj);
				});
			}
			ele.filterValues = allValues;
		});
	}
};

export const filterDataHandler = (
	data: any,
	showAssignedToMeRedux: boolean,
	loggedInUser: IUser | null,
	userIdKey?: string,
) => {
	const resultArr: any = [];

	data.forEach((ele: any) => {
		const tempArr: IApplyFilterParam = {
			key: '',
			values: [],
		};
		getDataOnClickOfAdvanceFilter(resultArr, ele, tempArr);
	});

	if (showAssignedToMeRedux) {
		resultArr.push({
			key: userIdKey || 'assignedUserId',
			values: userIdKey ? [loggedInUser?.email] : [loggedInUser?.userId],
		});
	}
	const updatedArray = extractValues(resultArr);
	return updatedArray;
};

export const getDataOnClickOfAdvanceFilter = (
	resultArr: any,
	ele: any,
	tempArr: any,
) => {
	if (resultArr.length > 0) {
		const indexData = resultArr.findIndex((obj: any) => obj.key === ele.type);
		if (indexData !== -1) {
			const val = resultArr[indexData].values;
			val.push(ele.label);
			resultArr[indexData].values = val;
		} else {
			tempArr.key = ele.type;
			tempArr.values.push(ele.label);
			resultArr.push(tempArr);
		}
	} else {
		tempArr.key = ele.type;
		tempArr.values.push(ele.label);
		resultArr.push(tempArr);
	}
};
export const fixedTwoDecimalOnlyPositiveValue = (value: any) => {
	const num = Math.floor(Math.abs(parseFloat(value)) * 100) / 100;
	return num.toFixed(2);
};

/**
 * @description method to trim the string with only spaces
 * @returns value
 */
export const trimSpaceHandler = (value: string) => value?.trim() === '';

/**
 * Formats a Date object into a string in 'YYYY-MM-DD HH:mm:ss' format.
 */
export const formatDateTime = (dateString: string) => {
	const date = new Date(dateString);

	const year = date.getUTCFullYear();
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	const day = String(date.getUTCDate()).padStart(2, '0');
	const hours = String(date.getUTCHours()).padStart(2, '0');
	const minutes = String(date.getUTCMinutes()).padStart(2, '0');
	const seconds = String(date.getUTCSeconds()).padStart(2, '0');

	return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const MB = 1048576;
export const KB = 1024;

/**
 * Converts a file size in bytes to a formatted string representation.
 * @param filesize - The size of the file in bytes.
 * @returns A formatted string representing the file size, with units in KB or MB.
 */
export const getFormattedSize = (filesize: number) => {
	const sizeInMB = filesize / MB;
	const sizeInKB = filesize / KB;
	return sizeInMB < 1 ? `${sizeInKB.toFixed(2)}KB` : `${sizeInMB.toFixed(2)}MB`;
};

export const validateEmail = (value: string) => {
	// Email validation regular expression
	const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
	return emailRegex.test(value) || 'Invalid email address';
};

/**
 * @description to restructure file data
 */
export const restructureDataForFile = (fileData: any, uploadProgress: any) => {
	fileData?.forEach((element: any, index: number) => {
		if (index === uploadProgress?.index) {
			element.progress = uploadProgress?.progress;
		}
	});
};

export const urlWithTenant = (url: string) => {
	const tenantId =
		localStorageWrapper.getStorageItem('tenantId') ?? DEFAULT_TENANT_ID;

	return `/${tenantId}${url}`;
};

export const getHeader = () => ({
	'Ocp-Apim-Subscription-Key': OCP_APIM_KEY,
	'id-token': `${storageWrapper.getStorageItem('id-token')}`,
});

export const downloadDocAndSave = async (fileName: string) => {
	const downloadUrl = getDownloadFileUrl(fileName);
	const response = await axios.get(downloadUrl, {
		responseType: 'blob',
		headers: getHeader(),
	});
	saveFile(response.data, fileName);
};
export const renderFeedbackSuccess = (
	showFeedbackSuccess: boolean,
	styles: any,
) => {
	if (showFeedbackSuccess) {
		return (
			<Typography className={styles}>{RESPONSE_SUBMITTED_TEXT}</Typography>
		);
	}
	return null;
};

/**
 * @description method to normalize white space
 * @param str
 */
export const normalizeWhitespace = (str: string) =>
	str?.replace(/\s+/g, ' ')?.trim();

/**
 * Converts a date string to an ISO 8601 formatted date string.
 * @param date - The date string to convert.
 * @returns The ISO 8601 formatted date string.
 */
export const getISODateString = (date: string) =>
	moment.utc(date).toISOString();

/**
 * Converts an ISO string to a date string.
 *
 * @param date - The ISO string representing a date.
 * @returns The date string extracted from the ISO string.
 */
export const getDateStringFromISOString = (date: string) => date.split('T')[0];

/**
 * @description method to split array based on size provided
 * @param array
 * @param size
 */
export const fetchBatchesFromArray = (array: any[], size: number) =>
	array?.reduce((acc: any, _, i: number) => {
		if (i % size === 0) acc?.push(array?.slice(i, i + size));
		return acc;
	}, []);

/**
 * @description method to determine if user has inventory approver
 * @param users
 */
export const hasInventoryApproverRole = (users: IUser | null) =>
	users?.roles?.some(
		(role: any) =>
			role?.roleName?.toLowerCase() === ROLE_INVENTORY_APPROVER.toLowerCase(),
	);

/**
 * @description method to determine if user has Inventory Manager
 * @param users
 */
export const hasInventoryManagerRole = (users: IUser | null) =>
	users?.roles?.some(
		(role: any) =>
			role?.roleName?.toLowerCase() === ROLE_INVENTORY_MANAGER.toLowerCase(),
	);

/**
 * @description method to determine if 2 array of string are equal
 * @param arr1
 * @param arr2
 */
export const arraysAreEqual = (arr1: any, arr2: string[]) =>
	arr1?.length === arr2?.length &&
	arr1?.every((value: string, index: number) => value === arr2[index]);

/**
 * @description method to convert strings to array of string
 * @param str
 * @return arrayOfStrings
 */
export const convertStringToArray = (str: string) => {
	let arrayOfStrings = str?.split(',');
	arrayOfStrings = arrayOfStrings?.map((item) => item?.trim());
	return arrayOfStrings;
};
export const getRelativeTime = (timestamp: any) => {
	const now = moment();
	const givenTime = moment.utc(timestamp).local();
	const duration = moment.duration(now.diff(givenTime));
	const seconds = duration.asSeconds();
	let timeString = '';

	if (seconds < 60) {
		timeString = `${Math.floor(seconds)} ${
			seconds === 1 ? 'second' : 'seconds'
		} ago`;
	} else if (seconds < 3600) {
		const minutes = Math.floor(seconds / 60);
		timeString = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
	} else if (seconds < 86400) {
		const hours = Math.floor(seconds / 3600);
		timeString = `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
	} else {
		const days = Math.floor(seconds / 86400);
		timeString = `${days} ${days === 1 ? 'day' : 'days'} ago`;
	}
	return timeString;
};

export const hasEmptyValues = (obj: any) =>
	Object.values(obj).some(
		(value) => value === null || value === undefined || value === '',
	);
export const convertObjectToQueryParam = (obj: any) =>
	Object.entries(obj)
		.map(([key, value]) => `${key}:${value}`)
		.join(',');
export const updateMap = (
	map: Map<string, string>,
	key: string,
	value: string,
) => {
	map.set(key, value);
	return map;
};

export const getFileType = (fileName: string) => {
	// Extract the part after the last dot (.)
	const extension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
	return extension.toLowerCase();
};
export const downloadBlobFileWithMultiFormat = (
	responseData: string,
	fileName: string,
) => {
	const fileType = getFileType(fileName);
	const byteCharacters = atob(responseData);
	const byteNumbers = Array.from(byteCharacters).map((char) =>
		char.charCodeAt(0),
	);
	const byteArray = new Uint8Array(byteNumbers);

	// Determine the MIME type based on the fileType
	const mimeType =
		mimeTypes[fileType as keyof typeof mimeTypes] || mimeTypes.xls;

	// Create a Blob from the byte array
	const blob = new Blob([byteArray], { type: mimeType });

	// Create a temporary URL for the Blob
	const url = URL.createObjectURL(blob);

	// Set the correct file name with the extension
	const downloadFileName = fileName.includes(`.${fileType}`)
		? fileName
		: `${fileName}.${fileType}`;

	// Create a temporary anchor element
	const link = document.createElement('a');
	link.href = url;
	link.download = downloadFileName;

	// Trigger the download
	document.body.appendChild(link);
	link.click();

	// Clean up
	document.body.removeChild(link);
};

/**
 * Checks if a new page is needed in the PDF document based on the current Y position and the length of the wrapped text.
 * If the current Y position plus the wrapped text length exceeds the page height minus the margin, a new page is added.
 *
 * @param wrappedTextLength - The length of the wrapped text to be added to the current page.
 */
export const checkNewPageNeeded = (
	wrappedTextLength: number,
	currentY: number,
	lineHeight: number,
	pageHeight: number,
	margin: number,
	doc: JSPDF,
) => {
	if (currentY + wrappedTextLength * lineHeight > pageHeight - margin) {
		doc.addPage();
		return margin;
	}
	return currentY;
};

/**
 * Calculates the Y coordinate for the next line of text based on the length of the text.
 * Updates the current Y coordinate by adding the height of the wrapped text and an additional margin.
 *
 * @param textLength - The length of the text to be wrapped.
 */
export const calculateYCoordinate = (
	textLength: number,
	lineHeight: number,
	currentY: number,
) => {
	const wrappedTextHeight = textLength * lineHeight;
	currentY += wrappedTextHeight + 10;
	return currentY;
};

/**
 * Splits the given text into an array of strings, each of which fits within the specified text width.
 *
 * @param text - The text to be wrapped. If the text is null or undefined, an empty string will be used.
 * @returns An array of strings, each of which fits within the specified text width.
 */
export const wrapText = (doc: JSPDF, textWidth: number, text = '') =>
	doc.splitTextToSize(text, textWidth);
